import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageSquare, AlertTriangle, Calendar, Megaphone, 
  Building2, ChevronRight, Bell, User, Clock,
  FileText, Users, Vote, Home
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubAccountDashboard = ({ user, token }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    unreadMessages: 0,
    activeIncidents: 0,
    upcomingEvents: 0,
    recentAnnouncements: 0
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [building, setBuilding] = useState(null);
  const [upcomingAG, setUpcomingAG] = useState(null);

  const permissions = user?.permissions || {};
  const isDelegate = user?.sub_account_type === 'delegue';
  const isTenant = user?.sub_account_type === 'locataire';

  useEffect(() => {
    loadDashboardData();
  }, [token]);

  const loadDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Load building info
      if (user?.building_ids?.length > 0) {
        try {
          const buildingRes = await axios.get(`${API}/buildings/${user.building_ids[0]}`, { headers });
          setBuilding(buildingRes.data);
        } catch (e) {
          console.log('Could not load building info');
        }
      }

      // Load messages
      try {
        const messagesRes = await axios.get(`${API}/messages`, { headers });
        const messages = messagesRes.data || [];
        setRecentMessages(messages.slice(0, 3));
        setStats(prev => ({ ...prev, unreadMessages: messages.filter(m => !m.read).length }));
      } catch (e) {
        console.log('Could not load messages');
      }

      // Load incidents (if permitted)
      if (permissions.can_view_incidents) {
        try {
          const incidentsRes = await axios.get(`${API}/incidents`, { headers });
          const incidents = incidentsRes.data || [];
          const activeIncidents = incidents.filter(i => i.status !== 'Résolu' && i.status !== 'Archivé');
          setRecentIncidents(activeIncidents.slice(0, 3));
          setStats(prev => ({ ...prev, activeIncidents: activeIncidents.length }));
        } catch (e) {
          console.log('Could not load incidents');
        }
      }

      // Load events
      try {
        const eventsRes = await axios.get(`${API}/events`, { headers });
        const events = eventsRes.data || [];
        const upcoming = events.filter(e => new Date(e.date) >= new Date()).slice(0, 3);
        setUpcomingEvents(upcoming);
        setStats(prev => ({ ...prev, upcomingEvents: upcoming.length }));
      } catch (e) {
        console.log('Could not load events');
      }

      // Load announcements
      try {
        const announcesRes = await axios.get(`${API}/announcements`, { headers });
        const announces = announcesRes.data || [];
        setAnnouncements(announces.slice(0, 3));
        setStats(prev => ({ ...prev, recentAnnouncements: announces.length }));
      } catch (e) {
        console.log('Could not load announcements');
      }

      // Load upcoming AG (if delegate)
      if (isDelegate && permissions.can_view_ag) {
        try {
          const agRes = await axios.get(`${API}/assemblees`, { headers });
          const ags = agRes.data || [];
          const upcoming = ags.find(ag => ag.status === 'Ouverte' || ag.status === 'En cours');
          setUpcomingAG(upcoming);
        } catch (e) {
          console.log('Could not load AGs');
        }
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subaccount-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#00D3A8] to-[#0099CC] rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              {getGreeting()}, {user?.prenom} !
            </h1>
            <p className="text-white/80 text-sm md:text-base">
              {isTenant ? '🏠 Espace Locataire' : '🗳️ Espace Mandataire'}
              {building && ` • ${building.nom}`}
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-2 bg-white/20 rounded-full px-4 py-2">
            <User size={18} />
            <span className="text-sm">{user?.email}</span>
          </div>
        </div>
      </div>

      {/* Building Info Card */}
      {building && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-[#00D3A8]/10 rounded-xl flex items-center justify-center">
              <Building2 className="text-[#00D3A8]" size={28} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800">{building.nom}</h3>
              <p className="text-sm text-gray-500">
                {building.adresse}
                {building.code_postal && `, ${building.code_postal}`}
                {building.ville && ` ${building.ville}`}
              </p>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p>{building.nombre_lots || '?'} lots</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          onClick={() => navigate('/messagerie')}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-2">
            <MessageSquare className="text-blue-500" size={24} />
            {stats.unreadMessages > 0 && (
              <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                {stats.unreadMessages}
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-800">{recentMessages.length}</p>
          <p className="text-xs text-gray-500">Messages</p>
        </div>

        {permissions.can_view_incidents && (
          <div 
            onClick={() => navigate('/incidents')}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-amber-500" size={24} />
              {stats.activeIncidents > 0 && (
                <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.activeIncidents}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.activeIncidents}</p>
            <p className="text-xs text-gray-500">Incidents actifs</p>
          </div>
        )}

        <div 
          onClick={() => navigate('/evenements')}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-2">
            <Calendar className="text-purple-500" size={24} />
          </div>
          <p className="text-2xl font-bold text-gray-800">{stats.upcomingEvents}</p>
          <p className="text-xs text-gray-500">Événements</p>
        </div>

        <div 
          onClick={() => navigate('/annonces')}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-2">
            <Megaphone className="text-green-500" size={24} />
          </div>
          <p className="text-2xl font-bold text-gray-800">{stats.recentAnnouncements}</p>
          <p className="text-xs text-gray-500">Annonces</p>
        </div>
      </div>

      {/* Delegate: Upcoming AG */}
      {isDelegate && permissions.can_view_ag && upcomingAG && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                <Vote className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium mb-1">Prochaine Assemblée Générale</p>
                <h3 className="font-semibold text-gray-800">{upcomingAG.titre}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  <Clock size={14} className="inline mr-1" />
                  {formatDate(upcomingAG.date_heure)} à {upcomingAG.heure || '—'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/assemblees')}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1"
            >
              <span>Voir l'AG</span>
              <ChevronRight size={16} />
            </button>
          </div>
          {permissions.can_vote && (
            <div className="mt-4 bg-white/50 rounded-lg p-3 text-sm text-purple-700">
              <Vote size={16} className="inline mr-2" />
              En tant que mandataire, vous pouvez voter aux résolutions avec la procuration du coproprietaire.
            </div>
          )}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Messages */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 flex items-center">
              <MessageSquare size={18} className="mr-2 text-blue-500" />
              Messages récents
            </h2>
            <button 
              onClick={() => navigate('/messagerie')}
              className="text-sm text-[#00D3A8] hover:underline"
            >
              Voir tout
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {recentMessages.length > 0 ? (
              recentMessages.map((msg) => (
                <div 
                  key={msg.id} 
                  onClick={() => navigate('/messagerie')}
                  className="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-gray-800 text-sm truncate">{msg.sender_name || 'Expéditeur'}</p>
                    <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">{msg.content}</p>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-gray-400">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucun message</p>
              </div>
            )}
          </div>
        </div>

        {/* Incidents (if tenant) */}
        {permissions.can_view_incidents && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 flex items-center">
                <AlertTriangle size={18} className="mr-2 text-amber-500" />
                Incidents en cours
              </h2>
              <button 
                onClick={() => navigate('/incidents')}
                className="text-sm text-[#00D3A8] hover:underline"
              >
                Voir tout
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {recentIncidents.length > 0 ? (
                recentIncidents.map((incident) => (
                  <div 
                    key={incident.id} 
                    onClick={() => navigate('/incidents')}
                    className="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-gray-800 text-sm truncate">{incident.titre}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        incident.status === 'En cours' ? 'bg-blue-100 text-blue-700' :
                        incident.status === 'En attente' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {incident.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{incident.description}</p>
                  </div>
                ))
              ) : (
                <div className="px-5 py-8 text-center text-gray-400">
                  <AlertTriangle size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun incident en cours</p>
                </div>
              )}
            </div>
            {permissions.can_create_incidents && (
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <button
                  onClick={() => navigate('/incidents')}
                  className="w-full bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  + Déclarer un incident
                </button>
              </div>
            )}
          </div>
        )}

        {/* Announcements */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 flex items-center">
              <Megaphone size={18} className="mr-2 text-green-500" />
              Annonces
            </h2>
            <button 
              onClick={() => navigate('/annonces')}
              className="text-sm text-[#00D3A8] hover:underline"
            >
              Voir tout
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {announcements.length > 0 ? (
              announcements.map((announce) => (
                <div 
                  key={announce.id} 
                  className="px-5 py-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-gray-800 text-sm">{announce.titre}</p>
                    {announce.priority === 'urgent' && (
                      <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">URGENT</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{announce.contenu}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(announce.created_at)}</p>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-gray-400">
                <Megaphone size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune annonce</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 flex items-center">
              <Calendar size={18} className="mr-2 text-purple-500" />
              Événements à venir
            </h2>
            <button 
              onClick={() => navigate('/evenements')}
              className="text-sm text-[#00D3A8] hover:underline"
            >
              Voir tout
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event) => (
                <div 
                  key={event.id} 
                  className="px-5 py-3"
                >
                  <div className="flex items-start space-x-3">
                    <div className="bg-purple-100 rounded-lg px-3 py-2 text-center min-w-[50px]">
                      <p className="text-xs text-purple-600">{new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' })}</p>
                      <p className="text-lg font-bold text-purple-700">{new Date(event.date).getDate()}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{event.titre}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {event.heure && `${event.heure} • `}{event.lieu || 'Lieu non précisé'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-gray-400">
                <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucun événement prévu</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Access Footer */}
      <div className="bg-gray-50 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Accès rapide</h3>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => navigate('/messagerie')}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <MessageSquare size={16} />
            <span>Messagerie</span>
          </button>
          {permissions.can_view_incidents && (
            <button 
              onClick={() => navigate('/incidents')}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <AlertTriangle size={16} />
              <span>Incidents</span>
            </button>
          )}
          <button 
            onClick={() => navigate('/annonces')}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Megaphone size={16} />
            <span>Annonces</span>
          </button>
          <button 
            onClick={() => navigate('/evenements')}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Calendar size={16} />
            <span>Événements</span>
          </button>
          {isDelegate && permissions.can_view_ag && (
            <button 
              onClick={() => navigate('/assemblees')}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Vote size={16} />
              <span>Assemblées</span>
            </button>
          )}
          <button 
            onClick={() => navigate('/mon-compte')}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <User size={16} />
            <span>Mon compte</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubAccountDashboard;
